import { IAtmOceanMeteogramItem } from "stores/data/atmOcean/atmOceanModel";
import { Units } from "stores/data/unitMeasurement/UnitMeasurementStore";
import { EnvironmentalVariables } from "../../../enum/EnvironmentalVariables";
import { VisualizationType } from "../../../enum/VisualizationType";
import { calculateAdjustedValue } from "./calculateAdjustedValue";
import { getSerieDefaultConfig } from "./common/getSerieDefaultConfig";
import { IProcessSerieParams } from "./common/types/IProcessSerieParams";
import { ISerieConfigMap } from "./common/types/ISerieConfigMap";

const getMaxHeightByEnvironmentalVariablesMapper = (
  series: IAtmOceanMeteogramItem[],
  selectedEnvironmentalVariables: EnvironmentalVariables[],
  configMap: Record<string, ISerieConfigMap>,
): Map<string, number> => {
  const maxByUnit = new Map<string, number>();

  for (const serie of series) {
    const environmentalVariable =
      serie.environmental_variable as EnvironmentalVariables;

    let max = -Infinity;

    const isVisible = selectedEnvironmentalVariables
      .filter(
        (variable) =>
          variable !== EnvironmentalVariables.WIND_DIR &&
          variable !== EnvironmentalVariables.WIND_DIR_80M &&
          variable !== EnvironmentalVariables.WIND_DIR_100M &&
          variable !== EnvironmentalVariables.CURRENT_DIR &&
          variable !== EnvironmentalVariables.WAVE_DIR &&
          variable !== EnvironmentalVariables.WAVE_DIR_SEA &&
          variable !== EnvironmentalVariables.WAVE_DIR_SWELL &&
          variable !== EnvironmentalVariables.PRECIP_PROBABILITY,
      )
      .includes(environmentalVariable);

    if (!isVisible || !max) continue;

    if (serie.data.length > 0 && Array.isArray(serie.data[0])) {
      for (const data of serie.data as number[][]) {
        let value = data[1];
        if (environmentalVariable === EnvironmentalVariables.PRECIP_MIN_20KM) {
          value = data[2];
        }

        if (value > max) {
          max = value;
        }
      }
    }

    const yAxisIndex = configMap[environmentalVariable]?.serieConfig?.yAxis ?? 0;
    const key = `${yAxisIndex}_${serie.units}`;

    if (maxByUnit.has(key)) {
      const existingMax = maxByUnit.get(key)!;
      maxByUnit.set(key, Math.max(existingMax, max));
    } else {
      maxByUnit.set(key, max);
    }
  }

  return maxByUnit;
};

export const processSerie = ({
  series,
  selectedEnvironmentalVariables,
  configMap,
  getUnitMeasurementConfig,
  addEnvironmentalVariable,
  visualizationType = VisualizationType.FORECAST,
  convertUnit,
}: IProcessSerieParams) => {
  let serieProcessed: any[] = [];
  let yAxisProcessed: any[] = [];
  let yAxisIndexRef = -1;

  const maxHeightByYIndexAndUnitMapper =
    getMaxHeightByEnvironmentalVariablesMapper(
      series,
      selectedEnvironmentalVariables,
      configMap,
    );
  for (const value of series) {
    const environmentalVariable =
      value.environmental_variable as unknown as EnvironmentalVariables;

    const isVisible = selectedEnvironmentalVariables.includes(
      environmentalVariable,
    );

    const isWaveHMax =
      environmentalVariable === EnvironmentalVariables.WAVE_HEIGHT_MAX;
    const isSensor = visualizationType === VisualizationType.SENSOR;

    let isVisibleOverride = false;
    if (isSensor) {
      // isVisibleOverride = true;
      if (isWaveHMax) {
        isVisibleOverride = false;
      }
    }

    const unitsUser =
      getUnitMeasurementConfig(environmentalVariable) || value.units_symbol;

    const serieDefaultConfig = getSerieDefaultConfig(
      environmentalVariable,
      value.data,
      unitsUser,
      addEnvironmentalVariable(environmentalVariable, visualizationType),
      visualizationType,
    );

    const environmentalVariableConfig: ISerieConfigMap =
      configMap[environmentalVariable];

    if (environmentalVariableConfig) {
      serieProcessed.push({
        ...serieDefaultConfig,
        ...environmentalVariableConfig.serieConfig,
        yAxisConfig: environmentalVariableConfig.yAxisConfig,
        tooltip: {
          ...serieDefaultConfig.tooltip,
          ...environmentalVariableConfig.serieConfig.tooltip,
        },
        visible: isVisibleOverride || isVisible,
        unitSymbol: value.units,
      });
    }

    if (!environmentalVariableConfig) {
      continue;
    }

    const yAxisConfig = environmentalVariableConfig?.yAxisConfig;

    const isUpadateYAxis =
      !!environmentalVariableConfig.serieConfig?.updateYAxis;

    if (isUpadateYAxis && yAxisConfig) {
      const yAxisIndex = environmentalVariableConfig.serieConfig?.yAxis;

      if (yAxisIndex === undefined) {
        throw new Error("yAxisIndex is required");
      }

      if (yAxisIndex && yAxisIndexRef === -1) {
        yAxisIndexRef = yAxisIndex;
      }

      const yAxisKey = `${yAxisIndex}_${value.units}`;
      const maxHeight = maxHeightByYIndexAndUnitMapper.get(yAxisKey);

      let yMax = yAxisConfig.max;
      let maxHeightConverted = maxHeight;

      if (
        unitsUser &&
        unitsUser.toLowerCase() !== value.units_symbol.toLowerCase()
      ) {
        yMax = convertUnit(
          yAxisConfig.max,
          value.units as Units,
          unitsUser as Units,
        );
      }

      serieProcessed[serieProcessed?.length - 1] = {
        ...serieProcessed[serieProcessed?.length - 1],
        defaultMaxConverted: yMax,
      };

      const adjustedValueMax = calculateAdjustedValue(yMax, maxHeightConverted);

      yAxisProcessed[yAxisIndex] = {
        ...environmentalVariableConfig.yAxisConfig,
        labels: {
          format: `{value} ${unitsUser}`,
          style: {
            color: "#000000",
          },
        },
        min: calculateAdjustedValue(
          environmentalVariableConfig.yAxisConfig?.min || 0,
          value?.min,
          false,
        ),
        max: adjustedValueMax,
        defaultMaxConverted: yMax,
      };
    }
  }

  if (!yAxisProcessed?.length) {
    yAxisProcessed = [
      {
        title: {
          text: "",
          style: {
            color: "#000000",
          },
        },
        min: 0,
        max: 50,
        labels: { format: "{value}", style: { color: "#000000" } },
        gridLineWidth: 1,
      },
    ];
  }

  // Fill sparse array holes with a default yAxis config so Highcharts
  // doesn't crash accessing properties on undefined entries (e.g. when
  // only PRECIP data exists at yAxis index 1 and index 0 is empty).
  const defaultYAxis = {
    title: { text: "", style: { color: "#000000" } },
    min: 0,
    max: 50,
    labels: { format: "{value}", style: { color: "#000000" } },
    gridLineWidth: 1,
    visible: false,
  };
  for (let i = 0; i < yAxisProcessed.length; i++) {
    if (!yAxisProcessed[i]) {
      yAxisProcessed[i] = defaultYAxis;
    }
  }

  return { serie: serieProcessed, yAxis: yAxisProcessed };
};
